<template>
  <div class="hazard-table">
    <div style="float: right; width: 20%">
      <div
        style="float: right; padding-top: 8px; padding-right: 8px"
        :id="`hazard-structure-${chemical.chemId}`"
      >
        <ChemCell
          v-if="chemical.chemId"
          :chemical="chemical"
          :show-images="true"
          :max-width="250"
          :img-max-height="250"
        />
        <!-- <a
          :href="getDashboardLink(this.chemical)"
          target="_blank"
        >
          <img
            :src="getStructureImageUrl(this.chemical, 200, 200)"
            alt=""
            class="t-img"
          />
        </a> -->
      </div>
    </div>

    <div style="float: left; width: 80%">
      <b-table-lite
        :fields="scoreRecordFields"
        :items="items"
        :stacked="hazardCategory && firstOnly"
        borderless
        small
      >
        <template #[`cell(${scoreField})`]="data">
          <button
            type="button"
            :id="`h-${data.item.hazardId}`"
            :class="{
              'table-td-hover': data && data.value !== 'ND',
              'table-secondary': data.value === 'I',
              'table-warning-light': data.value === 'M',
              'table-success': data.value === 'L',
              'table-danger': data.value === 'VH',
              'table-orange': data.value === 'H',
              'authoritative': (data.item.authority || data.item.finalAuthority) === 'Authoritative',
              'screening': (data.item.authority || data.item.finalAuthority) === 'Screening',
              'predicted': (data.item.authority || data.item.finalAuthority) === 'QSAR Model',
            }"
            class="px-2 hazard-cell"
            @click="showScoreRecordsTable(data.item.hazardName, data.item.hazardId, data.value)"
          >
            {{ data.value }}
            <HazardPopover
              v-if="data.value && data.value !== 'ND'"
              :delay="1000"
              :hazard-category="data.item.hazardId"
              :hazard-chemical="hazardChemicalSaved"
              :target="`h-${data.item.hazardId}`"
              :compact-popover="true"
              first-only
            />
          </button>
        </template>
        <template #cell()="data">
          <span class="visible-on-hover">
            {{ data.value }}
            <span v-if="data.field.key === 'hazardName'">
              <b-icon
                :id="`i-${data.item.hazardId}`"
                class="help"
                icon="info-circle"
              />
            </span>

            <b-popover
              :delay="delay"
              :target="`i-${data.item.hazardId}`"
              :triggers="triggers"
              custom-class="hazard-header-popover"
            >
              <template #title>
                {{ data.item.hazardName }}
              </template>
              <b-table-lite
                :fields="fields"
                :items="hazardLevels(data.item.hazardId)"
                hover
                small
              />
              <div>{{ hazardLevelsComments(data.item.hazardId) }}</div>
            </b-popover>
          </span>
        </template>
        <template #cell(note)="data">
          <span v-html="data.value" />
        </template>
        <template #cell(rationale)="data">
          <span v-html="data.value" />
        </template>
      </b-table-lite>
    </div>
    <ScoreRecordsModal
      v-model="showRecords"
      :hazard-name="hazardName"
      :score-records="scoreRecords"
      :chemical="chemical"
    />
  </div>
</template>
<script>
import { mapGetters } from 'vuex'
import { getStructureImageUrl, HAZARD_URL } from '@/main'
import HazardPopover from '@/components/hazard/HazardPopover.vue'
import ScoreRecordsModal from './ScoreRecordsModal.vue'
import ChemCell from '@/components/chemical/ChemCell.vue'

export default {
  components: { ChemCell, ScoreRecordsModal, HazardPopover },
  props: {
    triggers: {
      type: String,
      default: 'hover',
    },
    delay: {
      type: Number,
      default: 200,
    },
    firstOnly: {
      type: Boolean,
      default: false,
    },
    hazardChemical: {
      type: [Object, Promise, String],
      required: true,
    },
    hazardCategory: {
      type: String,
      default: null,
    },
    scoreRecordFields: {
      type: Array,
      default: function () {
        if (!this.hazardCategory) return ['hazardName', 'finalScore', 'finalScoreSource']
        else if (this.firstOnly)
          return [
            { key: 'source', sortable: true },
            { key: 'listType', label: 'Authority', sortable: true },
            { key: 'score', sortable: true },
            { key: 'route', sortable: true },
            { key: 'category', sortable: true },
            { key: 'hazardCode', sortable: true },
            { key: 'hazardStatement' },
            { key: 'rationale' },
            { key: 'note' },
          ]
        else
          return [
            { key: 'source', sortable: true },
            { key: 'authority', sortable: true },
            { key: 'score', sortable: true },
          ]
      },
    },
  },

  data() {
    return {
      chemical: {
        name: null,
        casrn: null,
      },
      hazardChemicalSaved: null,
      score: {
        hazardName: null,
        records: [],
      },
      scores: null,

      scoreStructure: null,
      hazardName: null,
      chemIndex: null,
      catIndex: null,
      scoreRecords: null,
      showRecords: false,
      fields: [
        { key: 'source', sortable: true },
        { key: 'endpoint' },
        { key: 'vh', label: 'VH' },
        { key: 'h' },
        { key: 'm' },
        { key: 'l' },
      ],
    }
  },

  computed: {
    ...mapGetters(['getHazardLevels']),
    items: function () {
      if (this.hazardCategory) return this.firstOnly ? this.score.records.slice(0, 1) : this.score.records
      else return this.scores
    },
    scoreField: function () {
      return this.hazardCategory ? 'score' : 'finalScore'
    },
    scoresAsMap: function () {
      return new Map(this.scores.map((score) => [score.hazardId, score.records]))
    },
  },

  methods: {
    getStructureImageUrl(chemical, width, height) {
      return getStructureImageUrl(chemical, width, height)
    },
    getDashboardLink(item) {
      return `https://comptox.epa.gov/dashboard/${item.sid}`
    },
    getHazardChemical(id) {
      return this.$axios.get(`${HAZARD_URL}?query=${encodeURIComponent(id)}`).then((response) => {
        return response.data.hazardChemicals[0]
      })
    },
    showScoreRecordsTable(hazardName, hazardId, value) {
      if (value && value !== 'ND') {
        this.hazardName = hazardName
        this.scoreRecords = this.scoresAsMap.get(hazardId)
        this.showRecords = true
      }
    },
    hazardLevels: function (hazardId) {
      return this.getHazardLevels.find((c) => c.categoryId === hazardId).sources
    },
    hazardLevelsComments: function (hazardId) {
      return this.getHazardLevels.find((c) => c.categoryId === hazardId).comments
    },
  },

  mounted() {
    let hazardChemical =
      typeof this.hazardChemical === 'string' ? this.getHazardChemical(this.hazardChemical) : this.hazardChemical

    this.hazardChemicalSaved = hazardChemical

    Promise.resolve(hazardChemical).then((hc) => {
      this.chemical = hc.chemical
      if (this.hazardCategory) this.score = hc.scores.find((s) => s.hazardId === this.hazardCategory)
      else this.scores = hc.scores
    })
  },
}
</script>

<style lang="scss">
.hazard-popover-compact {
  max-width: 600px !important;
  min-width: 400px;
  width: max-content !important;
}

.hazard-header-popover {
  max-width: 800px !important;
}

.hazard-cell {
  width: 38px;
  height: 38px;
  display: inline-block;
  text-align: center;
  align-content: center;
}
.hazard-table td {
  vertical-align: middle !important;
}
.hazard-table .text-end {
  height: 0 !important;
}

td:has(.visible-on-hover) svg {
  visibility: hidden;
}

td:has(.visible-on-hover):hover svg {
  visibility: visible;
  cursor: help;
}
</style>
